import React, { useRef, useState, useEffect } from 'react';
import PackageSectionThrill from './PackageSectionThrill';
import Banner from './Banner';
import ReviewSlides from './ReviewSlide';
import Header from './Header';
import LottiePlayer from './LottiePlayer';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTourPackagesByType } from '../redux/slices/packageSlice';
import { fetchContactNumbers } from '../redux/slices/contactNumberSlice';

const PackagesThrill = () => {
    const dispatch = useDispatch();
    const { contactNumbers } = useSelector((state) => state.contactNumbers);
    const [activeFilter, setActiveFilter] = useState('Domestic');
    const [viewMode, setViewMode] = useState('Tours');
    const [isLoading, setIsLoading] = useState(true);

    const filters = ['Domestic', 'International', 'Short Trip', 'Customised'];
    // Refs for scrolling to sections
    const domesticRef = useRef(null);
    const internationalRef = useRef(null);
    const customisedRef = useRef(null);
    const shortTripRef = useRef(null);
    const activitiesRef = useRef(null);

    // Fetch contact numbers
    useEffect(() => {
        dispatch(fetchContactNumbers());
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 1500);
        return () => clearTimeout(timer);
    }, [dispatch]);

    const handleFilterClick = (filter) => {
        if (filter === 'Tours') {
            setViewMode('Tours');
            setActiveFilter('Domestic');
            dispatch(fetchTourPackagesByType('Domestic'));
        } else if (filter === 'Activities') {
            setViewMode('Activities');
            setActiveFilter('Activities');
            dispatch(fetchTourPackagesByType('Activities'));

            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        } else {
            setViewMode('Tours');
            setActiveFilter(filter);
            dispatch(fetchTourPackagesByType(filter));

            const refMap = {
                Domestic: domesticRef,
                International: internationalRef,
                'Short Trip': shortTripRef,
                Customised: customisedRef,
                Activities: activitiesRef,
            };
            const targetRef = refMap[filter];
            if (targetRef?.current) {
                const offsetTop = targetRef.current.getBoundingClientRect().top + window.pageYOffset;
                const headerOffset = 100;
                window.scrollTo({
                    top: offsetTop - headerOffset,
                    behavior: 'smooth',
                });
            }
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <LottiePlayer
                    src="https://lottie.host/b2b25f7e-1811-405a-9986-2d09791950ef/8cJ75x5MYO.lottie"
                    width={300}
                    height={300}
                />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white">
            <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-20 py-8 max-w-7xl pt-2 mx-auto mb-20">
                {/* Header */}
                <Header />
                {/* Sticky Filters */}
                <div className="bg-custom-white shadow-sm p-4 sticky top-0 z-20 overflow-x-auto rounded-xl border-b-2 mt-2 mb-4">
                    <div className="flex justify-between items-center">
                        <div className="flex space-x-4">
                            {filters.map((filter) => (
                                <button
                                    key={filter}
                                    onClick={() => handleFilterClick(filter)}
                                    className={`filter-button py-2 px-4 text-sm font-medium whitespace-nowrap ${activeFilter === filter
                                        ? 'text-custom-blue border-b-2 border-custom-yellow'
                                        : 'text-custom-blue hover:bg-gray-100'
                                        } rounded-md transition-colors duration-200`}
                                >
                                    {filter}
                                </button>
                            ))}
                            <button
                                onClick={() => handleFilterClick('Tours')}
                                className={`border border-custom-blue text-custom-blue py-2 px-4 rounded-md text-sm font-medium ${viewMode === 'Tours'
                                    ? 'bg-custom-yellow text-custom-blue'
                                    : 'text-custom-blue hover:bg-gray-100'
                                    } rounded-md transition-colors duration-200`}
                            >
                                Tours
                            </button>
                            <button
                                onClick={() => handleFilterClick('Activities')}
                                className={`border border-custom-blue text-custom-blue py-2 px-4 rounded-md text-sm font-medium ${viewMode === 'Activities'
                                    ? 'bg-custom-yellow text-custom-blue'
                                    : 'hover:bg-custom-yellow hover:text-custom-blue'
                                    }`}
                            >
                                Activities
                            </button>
                        </div>
                        <div className="flex flex-col text-sm font-medium text-custom-blue space-y-1">
                            <a href={`tel:${contactNumbers[activeFilter]?.phone || '+916360204100'}`} className="flex items-center hover:underline">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                {contactNumbers[activeFilter]?.phone || '+91 63602 04100'}
                            </a>
                            <a href={`tel:${contactNumbers[activeFilter]?.phone2 || '+917204247400'}`} className="flex items-center hover:underline">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                {contactNumbers[activeFilter]?.phone2 || '+91 72042 47400'}
                            </a>
                        </div>
                    </div>
                </div>
                {viewMode === 'Tours' ? (
                    <>
                        <PackageSectionThrill title="Domestic Tours" tourType="Domestic" ref={domesticRef} contactNumbers={contactNumbers['Domestic']} />
                        <PackageSectionThrill title="International Tours" tourType="International" ref={internationalRef} contactNumbers={contactNumbers['International']} />
                        <Banner />
                        <div className="my-8" />
                        <PackageSectionThrill title="Customised Tours" tourType="Customised" ref={customisedRef} contactNumbers={contactNumbers['Customised']} />
                        <PackageSectionThrill title="Short Trips" tourType="Short" ref={shortTripRef} contactNumbers={contactNumbers['Short']} />
                        <ReviewSlides />
                    </>
                ) : (
                    <>
                        <PackageSectionThrill title="Activities" tourType="Activities" ref={activitiesRef} contactNumbers={contactNumbers['Activities']} />
                        <Banner />
                        <div className="my-8" />
                        <ReviewSlides />
                    </>
                )}
            </div>
        </div>
    );
};

export default PackagesThrill;